import React, { HTMLInputAutoCompleteAttribute } from "react";
import { FieldValues, Path } from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";

import { useAutofill } from "../../Utils/useAutofill";
import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";
import {
    useNumericField,
    useNumericFieldProps
} from "../NumericField/NumericField.hooks";

type NumericTextFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    placeholder?: string;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    disabled?: boolean;
    ref?: React.Ref<HTMLInputElement>;
    maxLength?: useNumericFieldProps<T>["maxLength"];
    onKeyDown?: useNumericFieldProps<T>["onKeyDown"];
    onKeyUp?: useNumericFieldProps<T>["onKeyUp"];
    onBlur?: (
        e: React.FocusEvent<HTMLInputElement>,
        originalOnBlur: () => void
    ) => void;
};

const NumericTextField = <T extends FieldValues>({
    name,
    label,
    microCopy,
    required,
    placeholder,
    autoComplete,
    disabled,
    hideLabel,
    hideMicroCopy,
    maxLength,
    onKeyDown,
    onKeyUp,
    onBlur,
    ref,
    test
}: NumericTextFieldProps<T>) => {
    const { handleOnAutofill } = useAutofill<T>(name);

    const {
        onKeyDownHandler,
        onKeyUpHandler,
        onChangeHandler,
        onPasteHandler
    } = useNumericField<T>({
        name,
        required,
        defaultValue: undefined,
        maxLength,
        onKeyDown,
        onKeyUp
    });

    return (
        <BaseField<T, Path<T>>
            name={name}
            label={label}
            microCopy={microCopy}
            required={required}
            hideLabel={hideLabel}
            hideMicroCopy={hideMicroCopy}
        >
            {({ field, fieldState }) => {
                return (
                    <Input
                        {...field}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        id={name}
                        onBlur={(e) => {
                            onBlur?.(e, field.onBlur);
                        }}
                        onKeyDown={onKeyDownHandler}
                        onKeyUp={onKeyUpHandler}
                        onPaste={onPasteHandler}
                        onChange={onChangeHandler(field.value, field.onChange)}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        valid={fieldState.isValid}
                        invalid={fieldState.isInvalid}
                        disabled={disabled}
                        data-test={test}
                        ref={ref}
                        onAutofill={handleOnAutofill}
                    />
                );
            }}
        </BaseField>
    );
};

export { NumericTextField };
